import { Box, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ChangeEvent } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { MediaDimensionValue } from '@/entities/creatives';
import {
  parseMediaDimensionValue,
  useRequestCreative,
} from '@/entities/creatives';
import { Analytics, isFinished, isLoading } from '@/shared/lib';
import { SearchInput, Checkbox, Button } from '@/shared/ui';
import type { MediaFilterParams } from '../../model/types';
import type { FilterChangeMeta } from '../../model/types';
import { StringFilterSelectionType } from '../../model/types';
import type { ContainsValue } from '../ContainsStringFilterCortrol/ContainsStringFilterCortrol';
import {
  ContainsOperator,
  ContainsStringFilterCortrol,
} from '../ContainsStringFilterCortrol/ContainsStringFilterCortrol';
import {
  FilterSelectionControlsProvider,
  useFilterSelectionControlsContext,
} from '../FilterSelectionControlsContext/FilterSelectionControlsContext';
import { FilterSelectionTypeSelect } from '../FilterSelectionTypeSelect/FilterSelectionTypeSelect';
import { useFiltersParamsContext } from '../FiltersParamsContext/FiltersParamsContext';
import { SelectedCountLabel } from '../SelectedCountLabel/SelectedCountLabel';
import type { ItemRendererProps } from '../VirtualizedFilter/VirtualizedFilter';
import { VirtualizedFilter } from '../VirtualizedFilter/VirtualizedFilter';

const DimensionIdContext = createContext('');

interface IncludeValue {
  type: StringFilterSelectionType.INCLUDE | StringFilterSelectionType.EXCLUDE;
  options: string[];
}

interface ExcludeValue {
  type: StringFilterSelectionType.EXCLUDE;
  options: string[];
}

type IncludeExcludeValue = IncludeValue | ExcludeValue;

export type Value = IncludeExcludeValue | ContainsValue;

interface Props {
  id: string;
  value?: Value;
  onChange: (v: Value, meta?: FilterChangeMeta) => void;
}

export function MediaFilter(props: Props) {
  const { paramsData, requestMediaFilterParams } = useFiltersParamsContext();
  const filterParamsLoadable = paramsData[props.id];
  const getOptionValue = (option: unknown) => {
    const parsed = parseMediaDimensionValue(option as string);
    return parsed?.[props.id as keyof MediaDimensionValue] ?? (option as string);
  };

  useEffect(
    () => {
      if (
        isFinished(filterParamsLoadable?.status) ||
        isLoading(filterParamsLoadable?.status)
      ) {
        return;
      }

      requestMediaFilterParams(props.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterParamsLoadable],
  );

  if (
    props.value?.type === StringFilterSelectionType.CONTAINS ||
    props.value?.type === StringFilterSelectionType.NOT_CONTAIN
  ) {
    return (
      <ContainsStringFilterCortrol
        {...props}
        getOptionValue={getOptionValue}
        value={props.value as ContainsValue}
      />
    );
  }

  return (
    <FilterSelectionControlsProvider
      id={props.id}
      value={props.value?.options || []}
      getOptionValue={getOptionValue}
    >
      <IncludeExcludeContent
        {...props}
        value={props.value as IncludeExcludeValue}
      />
    </FilterSelectionControlsProvider>
  );
}

interface IncludeExcludeProps {
  id: string;
  value?: IncludeExcludeValue;
  onChange: (v: Value, meta?: FilterChangeMeta) => void;
}

function IncludeExcludeContent({ id, value, onChange }: IncludeExcludeProps) {
  const {
    filterQuery,
    setFilterQuery,
    getSelectAllValue,
    getClearAllValue,
    isClearAllDisabled,
  } = useFilterSelectionControlsContext();
  const { paramsData } = useFiltersParamsContext();
  const filterParamsLoadable = paramsData[id];
  const filterParams = filterParamsLoadable?.params as
    | MediaFilterParams
    | undefined;
  const allOptions = useMemo(() => {
    return (filterParams?.options || []).map((opt) => {
      const parsed = parseMediaDimensionValue(opt);
      return parsed?.[id as keyof MediaDimensionValue] ?? opt;
    });
  }, [filterParams, id]);
  const notPresentOptions = useMemo(() => {
    const availableOptions = new Set(allOptions);

    return (value?.options || []).filter((option) => {
      return !availableOptions.has(option);
    });
  }, [value, allOptions]);

  useEffect(
    () => {
      if (isFinished(filterParamsLoadable?.status) && !value) {
        onChange(
          {
            type: StringFilterSelectionType.INCLUDE,
            options: allOptions,
          },
          { isProgrammatic: true },
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterParamsLoadable],
  );

  const handleTypeChange = (type: StringFilterSelectionType) => {
    if (
      type === StringFilterSelectionType.CONTAINS ||
      type === StringFilterSelectionType.NOT_CONTAIN
    ) {
      onChange({
        type,
        matchingStrings: [],
        operator: ContainsOperator.AND,
      });
    } else {
      onChange({
        type,
        options: value?.options || allOptions,
      });
    }
    Analytics.sendFeatureUsage('media_filter', 'type_change', {
      type,
    });
  };
  const handleOptionsChange = (options: string[]) => {
    onChange({
      type: value?.type || StringFilterSelectionType.INCLUDE,
      options,
    });
  };
  const handleClearAll = () => {
    if (!value) {
      return;
    }

    onChange({
      type: value.type,
      options: getClearAllValue(),
    });
  };
  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (!value) {
      return;
    }

    if (event.target.checked) {
      onChange({
        type: value.type,
        options: getSelectAllValue(),
      });
    } else {
      onChange({
        type: value.type,
        options: getClearAllValue(),
      });
    }
  };

  return (
    <div>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
        <FilterSelectionTypeSelect
          value={value?.type}
          onChange={handleTypeChange}
        />
        <SearchInput query={filterQuery} onChange={setFilterQuery} />
      </Box>
      <Divider />
      <Box
        sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
      >
        <Checkbox
          sx={{ p: 0 }}
          size="small"
          indeterminate={
            value?.options &&
            value?.options.length > 0 &&
            value?.options.length < allOptions.length
          }
          checked={allOptions.length === value?.options.length}
          onChange={handleSelectAll}
        />
        <SelectedCountLabel
          notPresetCount={notPresentOptions.length}
          optionsCount={value?.options.length || 0}
          totalCount={allOptions.length}
        />
        <Button
          sx={{ p: 0, lineHeight: '1.375rem', color: 'black' }}
          disabled={isClearAllDisabled(value?.options || [])}
          variant="text"
          color="secondary"
          onClick={handleClearAll}
        >
          Clear all
        </Button>
      </Box>
      <Divider />
      <DimensionIdContext.Provider value={id}>
        <VirtualizedFilter
          ItemRenderer={MediaOptionRenderer}
          value={value?.options}
          onChange={handleOptionsChange}
        />
      </DimensionIdContext.Provider>
    </div>
  );
}

const Image = styled('img')(() => ({
  height: 22,
  aspectRatio: '1 / 1',
  objectFit: 'contain',
}));

function MediaOptionRenderer({ value, setHeight }: ItemRendererProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const dimensionId = useContext(DimensionIdContext);
  const parsed = parseMediaDimensionValue(value as string);
  const creative = useRequestCreative(parsed);
  const thumbnailUrl = creative?.thumbnailUrl ?? '';
  const displayName =
    parsed?.[dimensionId as keyof typeof parsed] ?? (value as string);

  useEffect(
    () => {
      if (rootRef.current) {
        setHeight(rootRef.current.clientHeight);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rootRef],
  );

  return (
    <Box ref={rootRef} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {thumbnailUrl && <Image src={thumbnailUrl} />}{' '}
      <Box component="span" sx={{ wordBreak: 'break-word' }}>
        {displayName}
      </Box>
    </Box>
  );
}
